// FEC OpenFEC — outside (independent-expenditure) spending. Always proxied so
// the api_key stays server-side. Uses the by_candidate aggregate (support/oppose
// totals) plus a best-effort top-committees list. Blank ids → "not configured",
// and the finance hook then shows the config seed values (clearly badged).
import type { FinanceProvider } from '../../types/providers.ts';
import { ProviderError } from '../../types/providers.ts';
import type { CandidateFinance, FinanceResult, SpendByGroup } from '../../types/finance.ts';
import { fetchJson } from '../http.ts';
import { nowIso } from '../../lib/time.ts';

interface FecByCandRow {
  candidate_id?: string;
  support_oppose_indicator?: string; // 'S' | 'O'
  total?: number;
}
interface FecByCandResp {
  results?: FecByCandRow[];
}
interface FecScheduleERow {
  committee_name?: string;
  committee?: { name?: string };
  expenditure_amount?: number;
  support_oppose_indicator?: string;
}
interface FecScheduleEResp {
  results?: FecScheduleERow[];
}

export const fecProvider: FinanceProvider = {
  id: 'fec',
  kind: 'finance',
  async getFinance(race, cfg, signal) {
    const withIds = race.candidates.filter((c) => (c.fecCandidateIds?.length ?? 0) > 0);
    if (!withIds.length) {
      throw new ProviderError('not_configured', 'No FEC candidate ids configured');
    }

    const candidates: CandidateFinance[] = [];
    for (const c of withIds) {
      let supportTotal = 0;
      let opposeTotal = 0;
      const byGroup: SpendByGroup[] = [];

      for (const id of c.fecCandidateIds ?? []) {
        const agg = await fetchJson<FecByCandResp>({
          providerKey: 'fec',
          upstreamPath: `schedules/schedule_e/by_candidate?candidate_id=${encodeURIComponent(
            id,
          )}&cycle=${cfg.cycle}&election_full=true&per_page=100`,
          transport: 'proxy',
          signal,
        });
        for (const r of agg.results ?? []) {
          const t = Number(r.total) || 0;
          if (r.support_oppose_indicator === 'S') supportTotal += t;
          else if (r.support_oppose_indicator === 'O') opposeTotal += t;
        }

        // Top committees — best-effort; failure here doesn't fail the panel.
        try {
          const rows = await fetchJson<FecScheduleEResp>({
            providerKey: 'fec',
            upstreamPath: `schedules/schedule_e?candidate_id=${encodeURIComponent(
              id,
            )}&cycle=${cfg.cycle}&sort=-expenditure_amount&per_page=5`,
            transport: 'proxy',
            signal,
          });
          for (const r of rows.results ?? []) {
            const amount = Number(r.expenditure_amount) || 0;
            if (amount <= 0) continue;
            byGroup.push({
              committee: r.committee_name ?? r.committee?.name ?? 'Unknown committee',
              amount,
              indicator: r.support_oppose_indicator === 'O' ? 'oppose' : 'support',
            });
          }
        } catch {
          /* top-committees is optional */
        }
      }

      byGroup.sort((a, b) => b.amount - a.amount);
      candidates.push({ candidateId: c.id, supportTotal, opposeTotal, byGroup: byGroup.slice(0, 5) });
    }

    return {
      source: 'FEC',
      sourceId: 'fec',
      fetchedAt: nowIso(),
      cycle: cfg.cycle,
      candidates,
    } satisfies FinanceResult;
  },
};
